import { DataNode } from "antd/es/tree";

import NylasFolder from "@app/types/nylas/NylasFolder";

// Always-noise system folders — hidden regardless of the provider flag.
const EXCLUDED_SYSTEM_FOLDER_TYPES = new Set(["drafts", "junk", "spam", "trash"]);
// System folders that are meaningful record-sync boundaries and are kept even though they're provider-managed.
const SELECTABLE_SYSTEM_FOLDER_TYPES = new Set(["inbox", "sent"]);

// Gmail has no real folder hierarchy: a nested label is a single flat label whose NAME is the path
// ("New Folder/Sub level") and whose parentId is null. We recover the hierarchy from that path.
const LABEL_PATH_SEPARATOR = "/";

// A folder is offered for selection iff it's a user-created folder/label, or one of the few system
// folders that make sense as a record-sync boundary (Inbox, Sent). Everything else the provider
// manages — Archive, All Mail, Drafts/Trash/Spam/Junk, Gmail's CATEGORY_*/CHAT/IMPORTANT/UNREAD/star
// labels, Outlook's Conversation History/Outbox — is noise and hidden. `systemFolder` comes from
// Nylas; when it's absent (older grants) we treat the folder as user-created so a real folder is
// never hidden, still falling back to the Drafts/Trash/Spam/Junk denylist for the always-noise cases.
export const isSelectableFolder = (folder: NylasFolder): boolean => {
  const systemType = folder.systemFolderType?.toLowerCase();
  if (systemType && EXCLUDED_SYSTEM_FOLDER_TYPES.has(systemType)) {
    return false;
  }
  if (systemType && SELECTABLE_SYSTEM_FOLDER_TYPES.has(systemType)) {
    return true;
  }
  return folder.systemFolder !== true;
};

/**
 * Builds an antd checkable Tree from a flat folder list, nesting by parent.
 *
 * Nesting comes from one of two sources, resolved per folder:
 *  - a real `parentId` (Outlook/Microsoft), or
 *  - for a label with no usable `parentId` whose `name` is a `/`-path (Gmail), the label whose name
 *    is the parent path — e.g. "New Folder/Sub level" nests under "New Folder" and renders as just
 *    "Sub level". A `/`-named label with no matching parent label stays at root with its full name.
 *
 * Roots are folders with no resolved parent — including Outlook's hidden mailbox-root case, where
 * every top-level folder is parented to a node Nylas never returns (non-null `parentId` absent from
 * the list). A folder whose resolved parent IS in the response but was filtered out (a non-selectable
 * folder, e.g. a sub-folder of Trash) is pruned with its subtree, so excluded content never leaks in.
 *
 * Folders in `lockedFolderIds` render with a disabled checkbox (always-on, e.g. the mandatory Inbox).
 */
const buildFolderTree = (
  folders: NylasFolder[],
  lockedFolderIds: Set<NylasFolder["id"]> = new Set()
): DataNode[] => {
  const allFolderIds = new Set(folders.map((folder) => folder.id));
  const selectable = folders.filter(isSelectableFolder);
  const selectableIds = new Set(selectable.map((folder) => folder.id));
  // name -> id, used to resolve Gmail `/`-path parents (those labels carry no parentId).
  const idByName = new Map(selectable.map((folder) => [folder.name, folder.id]));

  // Resolve each folder's effective parent id and display title up front.
  const resolvedParentId = new Map<NylasFolder["id"], null | NylasFolder["id"]>();
  const titleById = new Map<NylasFolder["id"], string>();
  selectable.forEach((folder) => {
    titleById.set(folder.id, folder.name);

    // Real parent present in the response (Outlook) — use it as-is (may be a filtered folder, which
    // the grouping below prunes). Hidden-root parents (absent from the list) fall through to root.
    if (folder.parentId != null && allFolderIds.has(folder.parentId)) {
      resolvedParentId.set(folder.id, folder.parentId);
      return;
    }

    // Gmail `/`-path nesting: parent path = name up to the last "/".
    const separatorIndex = folder.name.lastIndexOf(LABEL_PATH_SEPARATOR);
    if (separatorIndex > 0) {
      const parentId = idByName.get(folder.name.slice(0, separatorIndex));
      if (parentId && parentId !== folder.id) {
        resolvedParentId.set(folder.id, parentId);
        titleById.set(folder.id, folder.name.slice(separatorIndex + LABEL_PATH_SEPARATOR.length));
        return;
      }
    }

    resolvedParentId.set(folder.id, null);
  });

  const childrenByParentId = new Map<NylasFolder["id"], NylasFolder[]>();
  const roots: NylasFolder[] = [];
  selectable.forEach((folder) => {
    const parentId = resolvedParentId.get(folder.id) ?? null;
    if (parentId == null) {
      roots.push(folder);
      return;
    }
    // Parent is in the response but was filtered out → this folder descends from a dropped folder;
    // prune it. (It's never reached from a root, so its own children fall away with it.)
    if (!selectableIds.has(parentId)) {
      return;
    }
    const siblings = childrenByParentId.get(parentId);
    if (siblings) {
      siblings.push(folder);
    } else {
      childrenByParentId.set(parentId, [folder]);
    }
  });

  const buildNode = (folder: NylasFolder): DataNode => {
    const title = titleById.get(folder.id) ?? folder.name;
    const disableCheckbox = lockedFolderIds.has(folder.id);
    const children = (childrenByParentId.get(folder.id) ?? []).map(buildNode);
    return children.length
      ? { children, disableCheckbox, key: folder.id, title }
      : { disableCheckbox, key: folder.id, title };
  };

  return roots.map(buildNode);
};

/** Flattens the keys of a built tree — the universe of selectable ids (leaves + parents). */
export const collectFolderKeys = (nodes: DataNode[]): NylasFolder["id"][] =>
  nodes.flatMap((node) => [
    String(node.key),
    ...(node.children ? collectFolderKeys(node.children) : []),
  ]);

/** The default selection (the Inbox folder ids) — matches the backend's Inbox-only default. */
export const getDefaultInboxSelection = (folders: NylasFolder[]): NylasFolder["id"][] => {
  // only seed ids that actually render in the tree, so the default can never point at a pruned node
  const renderable = new Set(collectFolderKeys(buildFolderTree(folders)));
  return folders
    .filter(
      (folder) =>
        renderable.has(folder.id) &&
        (folder.systemFolderType?.toLowerCase() === "inbox" ||
          folder.attributes.includes("\\Inbox"))
    )
    .map((folder) => folder.id);
};

export default buildFolderTree;
